import { Alert as ChakraAlert, Box, Link } from '@chakra-ui/react';
import { AlertCircle, CheckCircle2, Info, X, type LucideIcon } from 'lucide-react';
import * as React from 'react';

import { Button } from '../button';
import { IconButton } from '../button/icon-button';
import { type AlertStatus, alertIconSizePx } from './alert.kds';

const defaultStatusIcons: Record<AlertStatus, LucideIcon> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertCircle,
  error: AlertCircle,
};

export interface AlertLinkProps {
  label: string;
  href: string;
  target?: React.HTMLAttributeAnchorTarget;
}

export interface AlertActionProps {
  label: string;
  onClick: () => void;
}

export interface AlertProps extends Omit<ChakraAlert.RootProps, 'title' | 'variant' | 'status'> {
  status?: AlertStatus;
  title?: React.ReactNode;
  icon?: React.ReactElement;
  link?: AlertLinkProps;
  action?: AlertActionProps;
  closable?: boolean;
  onClose?: () => void;
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
  const { title, children, icon, link, action, closable, onClose, status = 'info', ...rest } = props;

  const DefaultIcon = defaultStatusIcons[status];
  const indicator = icon ?? <DefaultIcon size={alertIconSizePx} />;
  const hasDescription = React.Children.toArray(children).length > 0;
  const useContent = hasDescription || link;

  return (
    <ChakraAlert.Root ref={ref} status={status} {...rest}>
      <ChakraAlert.Indicator>{indicator}</ChakraAlert.Indicator>
      {useContent ? (
        <ChakraAlert.Content>
          {(title || hasDescription) && (
            <Box display="flex" flexDirection="column" gap={0}>
              {title && <ChakraAlert.Title>{title}</ChakraAlert.Title>}
              {hasDescription && <ChakraAlert.Description>{children}</ChakraAlert.Description>}
            </Box>
          )}
          {link && (
            <Link
              color="neutral.500"
              href={link.href}
              target={link.target}
              rel={link.target === '_blank' ? 'noopener noreferrer' : undefined}
              textDecoration="underline"
              textStyle="default.regular"
            >
              {link.label}
            </Link>
          )}
        </ChakraAlert.Content>
      ) : (
        title && <ChakraAlert.Title flex="1">{title}</ChakraAlert.Title>
      )}
      {action && (
        <Button colorPalette="neutral" flexShrink={0} size="rg" variant="outline" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
      {closable && (
        <IconButton
          aria-label="Close alert"
          icon={X}
          variant="ghost"
          size="md"
          opacity={0.4}
          flexShrink={0}
          onClick={onClose}
        />
      )}
    </ChakraAlert.Root>
  );
});
